import { DashboardFilterInstance, FilterValue } from "../../../context/FiltersProvider/types";
import {
	FilterCombinationPayload,
	FilterPayload,
	UserCustomFilterPayload,
} from "../../../datasources/api-client/data-contracts";
import { FilterItem, GroupFilter, RowFilter } from "../types";

type Params = {
	name: string;
	filters: DashboardFilterInstance[];
};

// Converts simple dashboard filters (chip-based UI) into a backend payload.
// Only "simple" mode filters are included.
// Everything is flattened and always combined with AND logic.
export const simpleFiltersToPayload = ({ name, filters }: Params): UserCustomFilterPayload => {
	const mappedFilters: FilterPayload[] = filters
		.filter((f) => f.mode === "simple")
		.map((f) => ({
			id: f.filterId,
			operator: f.operator as FilterPayload["operator"],
			value: f.value,
		}));

	return {
		name,
		isComplex: false,
		filter: {
			operator: "and",
			filters: mappedFilters,
		},
	};
};

// Normalizes row values to ensure backend-safe formatting.
// Removes null/undefined and keeps only valid primitives or arrays.
const normalizeRowValue = (value?: FilterValue): string | number | (string | number)[] | undefined => {
	if (value == null) return undefined;
	return value as any;
};

// Converts a single advanced form row into API payload format.
const mapRow = (row: RowFilter): FilterPayload => ({
	id: row.filter ?? "",
	operator: row.operator as FilterPayload["operator"],
	value: normalizeRowValue(row.value),
});

// Recursively converts a group node into a nested filter structure.
// Supports unlimited nesting with AND/OR logic preserved.
const mapGroup = (group: GroupFilter): FilterCombinationPayload => ({
	operator: group.rowOperator,
	filters: group.rows
		.map((item) => {
			if (item.type === "row") return mapRow(item.value);
			if (item.type === "group") return mapGroup(item.value);
			return null;
		})
		.filter(Boolean) as (FilterPayload | FilterCombinationPayload)[],
});

// Converts advanced UI form into backend payload for saving.
// Guarantees at least an AND empty structure if no root group exists.
export const advanceFiltersToPayload = ({
	name,
	filters,
}: {
	name: string;
	filters: FilterItem[];
}): UserCustomFilterPayload => {
	const root = filters.find((f) => f.type === "group");

	return {
		name,
		isComplex: true,
		filter: root?.type === "group" ? mapGroup(root.value) : { operator: "and", filters: [] },
	};
};

// Chooses between simple and advanced mapping when saving a filter.
export const filtersToPayload = ({
	name,
	mode,
	dashboardFilters,
	advancedForm,
}: {
	name: string;
	mode: "simple" | "advanced";
	dashboardFilters: DashboardFilterInstance[];
	advancedForm: FilterItem[];
}): UserCustomFilterPayload => {
	return mode === "advanced"
		? advanceFiltersToPayload({ name, filters: advancedForm })
		: simpleFiltersToPayload({ name, filters: dashboardFilters });
};

// Builds the runtime filter payload used when applying filters.
// This is NOT for persistence, only for execution.
export const buildAppliedFilterPayload = ({
	dashboardFilters,
	advancedForm,
	mode,
}: {
	dashboardFilters: DashboardFilterInstance[];
	advancedForm: FilterItem[];
	mode: "simple" | "advanced";
}): FilterCombinationPayload => {
	if (mode === "advanced") {
		return advanceFiltersToPayload({
			name: "",
			filters: advancedForm,
		}).filter;
	}

	return {
		operator: "and",
		filters: dashboardFilters
			.filter((f) => f.filterId && f.operator)
			.map((f) => ({
				id: f.filterId,
				operator: f.operator as FilterPayload["operator"],
				value: f.value,
			})),
	};
};
